/** @format */

import React, { useRef, useState, useEffect } from 'react'
import styled from 'styled-components'
import { _$, Table, TableColumn, Descriptions, DescriptionItem, Divider } from '@king-design/react'
import UserInfo from 'src/components/UserInfo'
import ExampleCodeViewer from '../ExampleCodeViewer'
import { renderFunctionStatus } from '../utils'
import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/material.css'
import 'codemirror/mode/python/python'
import 'codemirror/mode/shell/shell'

interface ScriptDesProps {
  className?: string
  functionList: any[]
  onVersionClick?: (record: any) => void
  onDeleteClick?: (record: any) => void
  onViewClick?: (record: any) => void
}

const ScriptDes: React.FC<ScriptDesProps> = ({
  className,
  functionList,
  onVersionClick,
  onDeleteClick,
  onViewClick
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [activeFunction, setActiveFunction] = useState<string>('')

  useEffect(() => {
    if (functionList.length > 0) {
      setActiveFunction(functionList[0]?.Sign || '')
    }
  }, [functionList])

  useEffect(() => {
    const handleScroll = () => {
      const scrollContainer = scrollRef.current?.querySelector('.main-content') as HTMLElement
      if (!scrollContainer) return

      const functionItems = functionList
        .map(item => ({
          sign: item.Sign,
          element: document.getElementById(item.Sign)
        }))
        .filter(item => item.element)

      const scrollTop = scrollContainer.scrollTop + 100 // Offset for better UX

      for (let i = functionItems.length - 1; i >= 0; i--) {
        const item = functionItems[i]
        if (item.element) {
          const elementTop = item.element.getBoundingClientRect().top + scrollContainer.scrollTop
          if (elementTop <= scrollTop) {
            setActiveFunction(item.sign)
            break
          }
        }
      }
    }

    const scrollContainer = scrollRef.current?.querySelector('.main-content')
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll)
      handleScroll() // Initial check
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll)
      }
    }
  }, [functionList])

  const renderFunctionItem = (item: any, index: number) => {
    const params = item.Extra?.Params || item.Extra?.params || []
    const lang = item.Lang === 'shell' ? 'shell' : 'python'

    return (
      <div key={item.ID} className="function-item" id={item.Sign}>
        {/* 函数标题 */}
        <div className="function-title-wrapper">
          <h2 className="function-title">{item.Sign}</h2>
          {/* 操作按钮区域 - 默认隐藏，hover 时显示 */}
          <div className="function-actions">
            <a
              onClick={() => {
                if (onViewClick) {
                  onViewClick(item)
                }
              }}
              className="action-link"
            >
              {_$('查看')}
            </a>
            <a
              onClick={() => {
                if (onVersionClick) {
                  onVersionClick(item)
                }
              }}
              className="action-link"
            >
              {_$('版本')}
            </a>
            {/* 系统预置函数不允许删除：IsPublic === 1 表示系统内置 */}
            {item.IsPublic !== 1 && (
              <a
                onClick={() => {
                  if (onDeleteClick) {
                    onDeleteClick(item)
                  }
                }}
                className="action-link action-link-danger"
              >
                {_$('删除')}
              </a>
            )}
          </div>
        </div>

        {/* 描述部分 */}
        {item.Remark && (
          <div className="description-section">
            <Descriptions columns={1}>
              <DescriptionItem label={_$('描述')}>{item.Remark}</DescriptionItem>
            </Descriptions>
          </div>
        )}

        {/* 元数据信息 - 使用 Descriptions 组件 */}
        <Descriptions columns={5} className="function-metadata">
          <DescriptionItem label={_$('语言')}>{item.Lang || '-'}</DescriptionItem>
          <DescriptionItem label={_$('创建者')}>
            {item.Creator ? <UserInfo name={item.Creator} /> : '-'}
          </DescriptionItem>
          <DescriptionItem label={_$('版本')}>{item.Version || '-'}</DescriptionItem>
          <DescriptionItem label={_$('类型')}>
            {item.IsPublic === 1 ? _$('系统预置') : _$('自定义')}
          </DescriptionItem>
          <DescriptionItem label={_$('状态')}>
            {renderFunctionStatus(item.Status, item.Approving)}
          </DescriptionItem>
        </Descriptions>

        {/* 参数部分 - 如果有参数则显示详细表格 */}
        {params.length > 0 ? (
          <div className="params-section">
            <h3 className="section-title">{_$('参数')}</h3>
            <Table
              data={params}
              rowKey={(record, idx) => `${record.Key || record.key}-${idx}`}
              animation={false}
              checkType="none"
              className="params-table"
            >
              <TableColumn
                key="key"
                title={_$('参数')}
                width="25%"
                slotTemplate={([record]) => record.Key || record.key || '-'}
              />
              <TableColumn
                key="type"
                title={_$('类型')}
                width="25%"
                slotTemplate={([record]) => record.Type || record.type || '-'}
              />
              <TableColumn
                key="default"
                title={_$('默认值')}
                width="25%"
                slotTemplate={([record]) => {
                  const defaultValue = record.Default || record.default
                  return defaultValue !== undefined && defaultValue !== null ? defaultValue : '-'
                }}
              />
              <TableColumn
                key="remark"
                title={_$('备注')}
                width="25%"
                slotTemplate={([record]) => {
                  const remark = record.Remark || record.remark
                  return remark || '-'
                }}
              />
            </Table>
          </div>
        ) : (
          <div className="params-section">
            <div className="params-section-content">
              <h3 className="section-title">{_$('参数')}</h3> &nbsp;&nbsp;&nbsp;<h3>-</h3>
            </div>
          </div>
        )}

        {/* 示例部分 */}
        {item.Extra?.Example && (
          <div className="example-section">
            <h3 className="section-title">{_$('例子')}</h3>
            <ExampleCodeViewer example={item.Extra.Example} lang={lang} />
          </div>
        )}

        <Divider />
      </div>
    )
  }

  return (
    <div className={className} ref={scrollRef}>
      <div className="doc-container">
        <div className="main-content">
          {functionList.map((item, index) => renderFunctionItem(item, index))}
        </div>
        <div className="anchor-sidebar">
          <div className="anchor-list">
            {functionList.map((item, index) => (
              <a
                key={item.ID}
                href={`#${item.Sign}`}
                className={`anchor-link-item ${activeFunction === item.Sign ? 'active' : ''}`}
                onClick={e => {
                  e.preventDefault()
                  setActiveFunction(item.Sign)
                  const element = document.getElementById(item.Sign)
                  if (element && scrollRef.current) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }
                }}
              >
                {item.Sign}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default styled(ScriptDes)`
  height: calc(100vh - 200px);
  overflow: hidden;
  background: #fff;

  .doc-container {
    display: flex;
    height: 100%;
    overflow: hidden;
  }

  .main-content {
    flex: 1;
    overflow-y: auto;
    padding: 24px 40px;
    height: 100%;
  }

  .anchor-sidebar {
    width: 220px;
    border-left: 1px solid #e0e0e0;
    padding: 16px;
    background: #fff;
    position: sticky;
    top: 0;
    height: 100%;
    overflow-y: auto;

    .anchor-list {
      .anchor-link-item {
        display: block;
        padding: 8px 0;
        color: #666;
        text-decoration: none;
        font-size: 14px;
        position: relative;
        padding-left: 16px;

        &:hover {
          color: #1890ff;
        }

        &.active {
          color: #1890ff;

          &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            width: 4px;
            height: 4px;
            background: #1890ff;
            border-radius: 50%;
          }
        }
      }
    }
  }

  .function-item {
    margin-bottom: 48px;
    padding-bottom: 24px;
    position: relative;

    &:hover .function-title-wrapper .function-actions {
      display: flex;
    }
  }

  .function-title-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    position: relative;

    .function-title {
      font-size: 20px;
      font-weight: 500;
      margin: 0;
      color: #262626;
      line-height: 1.6;
      flex: 1;
    }

    .function-actions {
      display: none;
      gap: 16px;
      align-items: center;
      margin-left: 16px;

      .action-link {
        color: #1890ff;
        cursor: pointer;
        font-size: 14px;
        text-decoration: none;
        transition: color 0.2s;

        &:hover {
          color: #40a9ff;
        }

        &.action-link-danger {
          color: #ff4d4f;

          &:hover {
            color: #ff7875;
          }
        }
      }
    }
  }

  .function-title {
    font-size: 20px;
    font-weight: 500;
    margin: 0 0 20px 0;
    color: #262626;
    line-height: 1.6;
  }

  .description-section {
    margin-bottom: 24px;

    .k-descriptions {
      .k-description-item {
        padding: 8px 0;
      }
    }
  }

  .function-metadata {
    margin-bottom: 24px;
    padding-bottom: 16px;

    .k-description-item {
      padding: 8px 0;
    }
  }

  .params-section,
  .example-section {
    margin-top: 24px;

    .section-title {
      font-size: 16px;
      font-weight: 500;
      margin: 0 0 16px 0;
      color: #262626;
    }
    .params-section-content {
      display: flex;
    }
  }

  .params-table {
    width: 100%;
    margin-top: 8px;

    .k-table {
      border: none;
    }

    .k-table-header {
      background: transparent;
    }

    .k-table-header th {
      border: none;
      border-bottom: 1px solid #e5e5e5;
      background: transparent;
      font-weight: 500;
      color: #262626;
      padding: 12px 8px;
    }

    .k-table-body td {
      border: none;
      border-bottom: 1px solid #f0f0f0;
      padding: 12px 8px;
    }

    .k-table-body tr:hover {
      background: #fafafa;
    }
  }

  .status-tag {
    display: inline-flex;
    align-items: center;
    border-radius: 4px;
    padding: 2px 8px;
    .k-icon {
      margin-right: 4px;
    }
  }
  .status-enabled {
    color: #1890ff;
    border-color: #91d5ff;
    background-color: #e6f7ff;
    .k-icon {
      color: #1890ff;
    }
  }
  .status-approving {
    color: #13c2c2;
    border-color: #13c2c2;
    background-color: #e6fffb;
    .k-icon {
      color: #13c2c2;
    }
  }
  .status-draft {
    color: #262626;
    border-color: #d9d9d9;
    background-color: #fff;
    .k-icon {
      color: #262626;
    }
  }
  .status-disabled {
    color: #ff4d4f;
    border-color: #ffccc7;
    background-color: #fff;
    .k-icon {
      color: #ff4d4f;
    }
  }
  .status-icon-spin {
    animation: spin 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
    display: inline-block;
  }
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`
